import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EllipsisVerticalIcon,
  Table,
  TableCell,
  TableRow,
} from '@fukuri-admins/ui/common';
import { iconButtonVariants } from '@fukuri-admins/ui/common/IconButton/logic';
import styles from './styles.module.scss';

/**
 * 利用者テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type UserRow = {
  /** 利用者の一意な識別子（API の user.id） */
  id: number;
  /** 所属導入企業名 */
  clientName: string;
  /** 利用者名 */
  name: string;
  /** メールアドレス */
  email: string;
  /** 店番（表示整形済み。未連携の場合は '-'） */
  branchCode: string;
  /** 口座番号（表示整形済み。未連携の場合は '-'） */
  accountNumber: string;
  /** 連携日時（表示整形済み。未連携の場合は '-'） */
  linkedAt: string;
  /** 利用開始日（表示整形済み。未設定の場合は '-'） */
  billableFrom: string;
  /** 作成日時（表示整形済み） */
  createdAt: string;
  /** 更新日時（表示整形済み） */
  updatedAt: string;
};

type Props = {
  /** テーブルに表示する利用者行データ */
  rows: UserRow[];
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onRowClick: (userId: number) => void;
};

/**
 * 利用者一覧テーブル（テーブル本体のみ）
 *
 * - 列構成: 利用企業名 / 名前 / メールアドレス / 店番 / 口座番号 / 連携日時 / 利用開始日 / 作成日時 / 更新日時 / 設定
 * - 利用開始日・更新日時列は常時表示する
 * - 設定アクション: 行内の DropdownMenu 経由で編集・削除コールバックを呼び出す
 * - ページャーは親の UserListBankAccountPresentation が担う
 */
export const UsersTable: React.FC<Props> = ({ rows, onEditUser, onDeleteUser, onRowClick }) => (
  <Table
    scrollable
    headRowClassName={styles.headRow}
    headers={[
      { label: '利用企業名', className: styles.clientNameCol },
      { label: '名前', className: styles.nameCol },
      { label: 'メールアドレス', className: styles.emailCol },
      { label: '店番', className: styles.branchCodeCol },
      { label: '口座番号', className: styles.accountNumberCol },
      { label: '連携日時', className: styles.linkedAtCol },
      { label: '利用開始日', className: styles.billableFromCol },
      { label: '作成日時', className: styles.createdAtCol },
      { label: '更新日時', className: styles.updatedAtCol },
      { label: '設定', className: styles.actionCol },
    ]}
  >
    {rows.map(row => (
      <TableRow key={row.id} onClick={() => onRowClick(row.id)}>
        <TableCell>{row.clientName}</TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell>{row.email ?? '-'}</TableCell>
        <TableCell>{row.branchCode}</TableCell>
        <TableCell>{row.accountNumber}</TableCell>
        <TableCell>{row.linkedAt}</TableCell>
        <TableCell>{row.billableFrom}</TableCell>
        <TableCell>{row.createdAt}</TableCell>
        <TableCell>{row.updatedAt}</TableCell>
        <TableCell align='center'>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button
                type='button'
                className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                aria-label='設定メニューを開く'
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVerticalIcon />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' onClick={e => e.stopPropagation()}>
              <DropdownMenuItem onSelect={() => onEditUser(row.id)}>編集</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={styles.deleteItem} onSelect={() => onDeleteUser(row.id)}>
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ))}
  </Table>
);
